import { useCallback, useEffect, useRef } from 'react';
import { BellOff, Loader2 } from 'lucide-react';
import { Header } from '@core/components/Header';
import { Card } from '@core/components/Card';
import {
  useNotifications,
  getNotificationIcon,
  formatNotificationTime
} from '@core/hooks/useNotifications';
import { Notification } from '@core/api/notifications';

export function NotificationsPage() {
  const {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    isMarkingAllAsRead
  } = useNotifications();

  const handleNotificationClick = useCallback(
    (notification: Notification) => {
      if (!notification.isRead) {
        markAsRead(notification.id);
      }
    },
    [markAsRead]
  );

  // Авто-прочтение: вход в уведомления => все считаются прочитанными (сбрасывает бейдж на колокольчике).
  // Срабатывает один раз за визит; после mutation unreadCount станет 0 и условие больше не выполнится.
  const didAutoMark = useRef(false);
  useEffect(() => {
    if (!isLoading && unreadCount > 0 && !didAutoMark.current && !isMarkingAllAsRead) {
      didAutoMark.current = true;
      markAllAsRead();
    }
  }, [isLoading, unreadCount, isMarkingAllAsRead, markAllAsRead]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-text-main">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-page to-page/95">
      <Header title="Уведомления" showNotifications={false} />

      <div className="p-4 space-y-4">
        {/* Notifications List (все отмечаются прочитанными автоматически при входе) */}
        {notifications.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClick={handleNotificationClick}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Notification Item Component
// ============================================================================

interface NotificationItemProps {
  notification: Notification;
  onClick: (notification: Notification) => void;
}

function NotificationItem({ notification, onClick }: NotificationItemProps) {
  const icon = getNotificationIcon(notification.type);
  const timeAgo = formatNotificationTime(notification.createdAt);

  return (
    <Card
      onClick={() => onClick(notification)}
      className={`cursor-pointer transition-all duration-200 ${
        notification.isRead
          ? 'bg-card/50 opacity-70'
          : 'bg-card border-l-4 border-l-primary shadow-md'
      }`}
    >
      <div className="flex gap-3">
        {/* Icon */}
        <div
          className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl shrink-0 ${
            notification.isRead
              ? 'bg-card'
              : 'bg-gradient-to-br from-primary/20 to-primary/10'
          }`}
        >
          {icon}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {notification.title && (
            <h3
              className={`font-semibold text-text-main mb-0.5 ${
                notification.isRead ? 'font-medium' : 'font-bold'
              }`}
            >
              {notification.title}
            </h3>
          )}
          <p
            className={`text-sm whitespace-pre-line ${
              notification.isRead ? 'text-text-main/50' : 'text-text-main/80'
            }`}
          >
            {notification.content}
          </p>
          <span className="text-xs text-text-main/40 mt-1 block">{timeAgo}</span>
        </div>

        {/* Unread indicator */}
        {!notification.isRead && (
          <div className="shrink-0 flex items-start pt-1">
            <div className="w-2.5 h-2.5 rounded-full bg-primary animate-pulse" />
          </div>
        )}
      </div>
    </Card>
  );
}

// ============================================================================
// Empty State Component
// ============================================================================

function EmptyState() {
  return (
    <Card className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-20 h-20 rounded-full bg-card flex items-center justify-center mb-4">
        <BellOff className="w-10 h-10 text-text-main/30" />
      </div>
      <h3 className="text-lg font-semibold text-text-main mb-2">
        Нет уведомлений
      </h3>
      <p className="text-sm text-text-main/60 max-w-[240px]">
        Здесь будут появляться уведомления о бонусах, акциях и подарках
      </p>
    </Card>
  );
}
